import { useId, useMemo } from 'react';
import { useIntl } from 'react-intl';

import type { UseSourceCohortsCardResult } from '../types';
import { useAnalysisRouteParams } from './useAnalysisRouteParams';
import { usePersistedExpansion } from './usePersistedExpansion';
import { useSourceCohortSection } from './useSourceCohortSection';
import { useStudyPeriodSection } from './useStudyPeriodSection';

// Remembered in this browser for each analysis separately: collapsing the cohorts on
// one analysis does not collapse them on the others.
const getExpansionStorageKey = (analysisProfileId: string) =>
  `analysis.sourceCohortsCard.expanded.${analysisProfileId}`;

// Composes what the cohorts card shows: the source cohorts as a list (the section
// hook yields the one cohort an analysis has today), the study period they share,
// and whether the card is open.
export const useSourceCohortsCard = (parentLoading = false): UseSourceCohortsCardResult => {
  const { formatMessage } = useIntl();
  const contentId = useId();
  const cohortSection = useSourceCohortSection(parentLoading);
  const studyPeriod = useStudyPeriodSection(parentLoading);
  const { analysisProfileId } = useAnalysisRouteParams();
  const { isExpanded, onToggle } = usePersistedExpansion(getExpansionStorageKey(analysisProfileId));

  const { cohort } = cohortSection;
  const cohorts = useMemo(() => (cohort ? [cohort] : []), [cohort]);

  return {
    projectId: cohortSection.projectId,
    title: formatMessage({ id: 'analysisDetail.sourceCohorts.title' }),
    cohorts,
    isLoading: cohortSection.isLoading || studyPeriod.isLoading,
    isExpanded,
    onToggle,
    toggleLabel: formatMessage({
      id: isExpanded
        ? 'analysisDetail.sourceCohorts.collapse'
        : 'analysisDetail.sourceCohorts.expand',
    }),
    contentId,
    studyPeriodLabel: studyPeriod.sectionLabel,
    studyPeriodText: studyPeriod.isSet ? studyPeriod.dateRangeText : studyPeriod.notSetText,
    isStudyPeriodSet: studyPeriod.isSet,
    notSelectedText: cohortSection.notSelectedText,
    staleCohortTooltip: cohortSection.staleCohortTooltip,
    switchLabel: cohortSection.switchLabel,
    onSwitchCohort: cohortSection.onAddCohort,
    isEditDisabled: cohortSection.isEditDisabled,
    editLockTooltip: cohortSection.editLockTooltip,
    isPickerOpen: cohortSection.isPickerOpen,
    onClosePicker: cohortSection.onClosePicker,
  };
};
